import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Tables } from "@how/database";
import { supabase } from "../../lib/supabase";

export type ErrorEventRow = Pick<
  Tables<"error_events">,
  | "id"
  | "reference"
  | "tenant_id"
  | "user_id"
  | "surface"
  | "route"
  | "error_class"
  | "message"
  | "stack"
  | "release"
  | "correlation_id"
  | "occurred_at"
  | "received_at"
> & { readonly tenant: { readonly name: string; readonly slug: string } | null };

/** The surfaces error_events accepts (its check constraint), with the words the screen uses for them. */
export const SURFACE_LABEL = {
  control_plane: "Control plane",
  staff_dashboard: "Staff dashboard",
  member_portal: "Member portal",
  public_web: "Public website",
  edge_function: "Edge Function",
} as const;

export type ErrorSurface = keyof typeof SURFACE_LABEL;

export function isErrorSurface(value: string): value is ErrorSurface {
  return Object.hasOwn(SURFACE_LABEL, value);
}

/** The church filter's value for errors that happened with no church in the address (sign-in, the control plane). */
export const NO_CHURCH = "none";

// Crockford's base 32, the alphabet newErrorReference draws from.
const CROCKFORD = /^[0-9A-HJKMNP-TV-Z]{8}$/;

/**
 * The reference as it is stored, E-XXXX-XXXX, from whatever support typed or heard: any case, with or without the
 * E and the dashes, with spaces. Crockford's decoding rules apply, so I and L read as 1 and O as 0, which is what a
 * person reading the reference aloud most often gets wrong. null when it cannot be a reference.
 */
export function normalizeErrorReference(input: string): string | null {
  let chars = input.toUpperCase().replace(/[^0-9A-Z]/g, "");
  if (chars.length === 9 && chars.startsWith("E")) chars = chars.slice(1);
  chars = chars.replace(/[IL]/g, "1").replace(/O/g, "0");
  if (!CROCKFORD.test(chars)) return null;
  return `E-${chars.slice(0, 4)}-${chars.slice(4)}`;
}

export interface ErrorEventsQuery {
  /** A normalized reference. When given, it is the only filter: a lookup must not be hidden by the others. */
  readonly reference: string | null;
  /** A tenant id, NO_CHURCH, or null for every church. */
  readonly church: string | null;
  readonly surface: ErrorSurface | null;
  readonly pageIndex: number;
  readonly pageSize: number;
}

/**
 * Recent error events, newest first, filtered and paginated on the server (RLS: platform staff read every row, and
 * nobody else reads any). The table holds at most the retention window, 30 days by default.
 */
export function useErrorEvents(q: ErrorEventsQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["error-events", q],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: ErrorEventRow[]; total: number }> => {
      const from = q.pageIndex * q.pageSize;
      let query = supabase
        .from("error_events")
        .select(
          "id, reference, tenant_id, user_id, surface, route, error_class, message, stack, release, correlation_id, occurred_at, received_at, tenant:tenants(name, slug)",
          { count: "exact" },
        )
        .order("received_at", { ascending: false })
        .range(from, from + q.pageSize - 1);
      if (q.reference !== null) {
        query = query.eq("reference", q.reference);
      } else {
        if (q.church === NO_CHURCH) query = query.is("tenant_id", null);
        else if (q.church !== null) query = query.eq("tenant_id", q.church);
        if (q.surface !== null) query = query.eq("surface", q.surface);
      }
      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: data, total: count ?? 0 };
    },
  });
}

/** Every church, for the filter. Deleted ones stay listed: their errors are kept until the prune. */
export function useChurchOptions() {
  return useQuery({
    queryKey: ["error-events", "churches"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tenants").select("id, name, slug").order("name");
      if (error) throw error;
      return data;
    },
  });
}
